'use client';

import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  overflow: visible;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #000000;
`;

const TextAreaWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
`;

const StyledTextArea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  max-width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  color: #111827;
  background: #ffffff;
  border: 1px solid ${({ $hasError }) => ($hasError ? '#ef4444' : '#d1d5db')};
  border-radius: 8px;
  outline: none;
  resize: vertical;
  min-height: 100px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  transition: border-color 0.2s;
  box-sizing: border-box;
  overflow-x: auto;
  word-wrap: break-word;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &:focus {
    border-color: #FB6F2A;
    outline-offset: 0;
  }
  
  &:hover:not(:focus) {
    border-color: #9ca3af;
  }
  
  &:disabled {
    background-color: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const CharacterCount = styled.div<{ $isOverLimit?: boolean }>`
  align-self: flex-end;
  font-size: 12px;
  color: ${({ $isOverLimit }) => ($isOverLimit ? '#ef4444' : '#6b7280')};
  margin-top: 4px;
`;

const ErrorText = styled.span`
  font-size: 13px;
  color: #ef4444;
  margin-top: 4px;
`;

const HelperText = styled.span`
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
`;

interface TextAreaProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  showCharCount?: boolean;
  rows?: number;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  helperIcon?: React.ReactNode;
  className?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  showCharCount = false,
  rows = 4,
  disabled = false,
  error,
  helperText,
  helperIcon,
  className,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (maxLength === undefined || newValue?.length <= maxLength) {
      onChange(newValue);
    }
  };

  const isOverLimit = maxLength !== undefined && value?.length > maxLength;

  return (
    <Container className={className}>
      {label && (
        <Label>
          {label}
          {helperIcon}
        </Label>
      )}
      <TextAreaWrapper>
        <StyledTextArea
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          disabled={disabled}
          $hasError={!!error}
          aria-label={label}
          aria-invalid={!!error}
          aria-describedby={error ? `${label}-error` : undefined}
        />
        {showCharCount && maxLength && (
          <CharacterCount $isOverLimit={isOverLimit}>
            {value?.length} / {maxLength}
          </CharacterCount>
        )}
      </TextAreaWrapper>
      {error && <ErrorText id={`${label}-error`}>{error}</ErrorText>}
      {helperText && !error && <HelperText>{helperText}</HelperText>}
    </Container>
  );
};

